import {useState, useEffect, useRef} from 'react'
import Checkbox from '@mui/material/Checkbox'
import PropTypes from 'prop-types'
import Autocomplete from '@mui/material/Autocomplete'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import Chip from '@mui/material/Chip'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import Grid from '@mui/material/Grid'
import {
  StyledTextField,
  StyledAutocompletePopperWide,
  StyledInput,
  StyledPopperWide,
  StyledSearchIcon,
  StyledBox,
  ErrorMessage,
  StyledSpan,
  StyledList,
  SelectedItemsContainer,
  StyledChipIconButton,
  StyledGridItemRelative,
  StyledDropdownContainer,
  StyledChipsContainer,
  StyledChip
} from './MultiSelectDropdownComponentStyle'
import {Close} from '@mui/icons-material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import {ClickAwayListener, Box} from '@mui/material'
import {InputTitleItalic} from '../TitleStyle'
import {StyledCustomLabels} from '../../AdminPanelStyling'
import {formatPOIs, formatSectionFilters, SECTIONS} from '../../customSettings/Customization/helper'
import {useFilterConfig, usePOIConfig} from '../../../store/hooks/config'

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />
const checkedIcon = <CheckBoxIcon fontSize='small' />

function PopperComponentWide(props) {
  const {...other} = props
  return <StyledAutocompletePopperWide {...other} />
}

PopperComponentWide.propTypes = {
  disablePortal: PropTypes.bool,
  open: PropTypes.bool.isRequired
}

const MultiSelectDropdownComponent = ({
  selectAllTitle,
  dropDownTitle,
  inputAndPreviewTitle,
  dropDownModifiedTitle,
  dropDownData,
  val,
  setVal,
  isCompleted,
  handleOptionDeletion,
  section,
  conditionalProps = {},
  setCustomLabelDialogProps,
  hasAnyFilters = false,
  isFilterDropdown = false,
  showCustomLabels = false
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const [isError, setIsError] = useState('')

  const filterConfig = useFilterConfig()
  const poiConfig = usePOIConfig()

  useEffect(() => {
    if (val.length >= 5 && inputAndPreviewTitle === 'Points of Interest') {
      setIsError('Max 5 POIs have been reached.')
    } else {
      setIsError('')
    }
  }, [val.length, inputAndPreviewTitle])

  const EntireUntitedStatesPill = (
    <Chip
      variant='variant1'
      key='Entire United States'
      label='Entire United States'
      aria-label='Entire United States'
      deleteIcon={
        <StyledChipIconButton aria-label={`Delete-Entire United States`}>
          <Close variant='closeIcon' onKeyDown={(event) => onChipDelete(event, 'Entire United States')} />
        </StyledChipIconButton>
      }
      onDelete={(event) => onChipDelete(event, 'Entire United States')}
    />
  )
  const onChipDelete = (event, option) => {
    if ((event.key === 'Enter' && event.type === 'keydown') || event.type === 'click') {
      if (option === 'Entire United States') {
        setVal([])
      } else if (inputAndPreviewTitle === 'Health Conditions') {
        handleOptionDeletion(inputAndPreviewTitle, val, option)
      } else {
        setVal(val.filter((entry) => entry !== option))
      }
    }
  }

  const pills = val.map((option) => {
    // This is to handle new options added by the user (allowed by freeSolo prop).
    const label = option.title || option

    return (
      <StyledChip
        variant='variant1'
        key={label}
        label={label}
        aria-label={label}
        deleteIcon={
          <StyledChipIconButton aria-label={`Delete ${label}`}>
            <Close
              variant='closeIcon'
              onKeyDown={(event) => onChipDelete(event, option)}
              data-testid={`Delete ${label}`}
            />
          </StyledChipIconButton>
        }
        onDelete={(event) => onChipDelete(event, option)}
      />
    )
  })

  const handleClose = () => {
    setOpen(false)
  }

  const [open, setOpen] = useState(false)

  const handleClick = () => {
    setOpen(!open)
  }

  const autocompleteRef = useRef(null)
  useEffect(() => {
    if (open) {
      autocompleteRef.current?.focus()
    }
  }, [open])

  const id = open ? 'admin-label' : undefined
  const allSelected = dropDownData.length === val.length
  const selectorTitle = () => {
    if (allSelected && selectAllTitle === 'Entire United States') {
      return selectAllTitle
    } else if (val.length === 0) {
      return dropDownTitle
    } else {
      return `${dropDownModifiedTitle} (${val.length})`
    }
  }

  const label = `${inputAndPreviewTitle.replaceAll('(s)', 's')}`

  const getAriaLabel = () => {
    switch (inputAndPreviewTitle) {
      case 'Jurisdiction(s)':
        return 'select jurisdiction'
      case 'Benefits':
        return 'select benefit'
      case 'Health Conditions':
        return 'select health condition'
      case 'Points of Interest':
        return 'select point of interest'
    }
  }

  const getDropdownOptions = () => {
    return dropDownData?.length > 1 && inputAndPreviewTitle !== 'Points of Interest'
      ? [{title: selectAllTitle, value: 'select-all'}, ...dropDownData]
      : dropDownData
  }

  return (
    <Grid container spacing={2}>
      <Grid item md={12} lg={6}>
        <StyledGridItemRelative>
          <StyledDropdownContainer>
            <StyledTextField
              variant='outlined'
              label={inputAndPreviewTitle}
              value={selectorTitle()}
              onClick={handleClick}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  handleClick()
                } else if (event.key === 'Escape' && open) {
                  handleClose()
                }
              }}
              InputProps={{
                readOnly: true,
                endAdornment: <KeyboardArrowDownIcon />
              }}
              hasError={isFilterDropdown && isCompleted && val.length === 0 && !hasAnyFilters}
              aria-label={`selected-${label}-dropdown-${open ? 'expanded' : 'collapsed'}`}
            />
          </StyledDropdownContainer>
          {open && (
            <ClickAwayListener onClickAway={handleClose}>
              <StyledPopperWide ref={autocompleteRef} id={id} placement='bottom'>
                <Autocomplete
                  open
                  multiple
                  value={val}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                      handleClose()
                    }
                  }}
                  onChange={(event, newValue, reason) => {
                    if (event.type === 'keydown' && event.key === 'Backspace' && reason === 'removeOption') {
                      return
                    }
                    if (newValue.some((option) => option.value === 'select-all')) {
                      if (allSelected) {
                        inputAndPreviewTitle === 'Health Conditions'
                          ? handleOptionDeletion(inputAndPreviewTitle, val, val)
                          : setVal([])
                      } else {
                        setVal(dropDownData)
                      }
                    } else if (inputAndPreviewTitle === 'Health Conditions') {
                      const option = val.find((entry) => !newValue.includes(entry))
                      option ? handleOptionDeletion(inputAndPreviewTitle, val, option) : setVal(newValue)
                    } else {
                      setVal(newValue)
                    }
                  }}
                  disableCloseOnSelect
                  PopperComponent={PopperComponentWide}
                  renderTags={() => null}
                  noOptionsText={
                    <Box key={label} role='alert' aria-live='polite' tabIndex={0}>
                      No labels
                    </Box>
                  }
                  renderOption={(props, option, {selected}) => {
                    const selectAllProps =
                      option.value === 'select-all' // To control the state of 'select-all' checkbox
                        ? {checked: allSelected}
                        : {}
                    const isLast = dropDownData.indexOf(option) === dropDownData.length - 1
                    const isDisabled =
                      isError !== '' &&
                      !val.some((entry) => {
                        return entry.title === option.title
                      })
                    return (
                      <>
                        <StyledList {...props} isDisabled={isDisabled} aria-label={option.title}>
                          <Checkbox
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault()
                                event.target.click()
                              } else if (event.key === 'Escape') {
                                handleClose()
                              }
                            }}
                            icon={icon}
                            checkedIcon={checkedIcon}
                            checked={selected}
                            {...selectAllProps}
                          />
                          <StyledSpan>{option.title}</StyledSpan>
                        </StyledList>
                        {isError !== '' && isLast && <ErrorMessage>{isError}</ErrorMessage>}
                      </>
                    )
                  }}
                  options={getDropdownOptions()}
                  getOptionLabel={(option) => option.title}
                  {...conditionalProps}
                  renderInput={(params) => (
                    <StyledBox isFocused={isFocused}>
                      <StyledSearchIcon />
                      <StyledInput
                        ref={params.InputProps.ref}
                        inputProps={{
                          ...params.inputProps,
                          'aria-label': getAriaLabel()
                        }}
                        placeholder='Search'
                        onFocus={() => {
                          setIsFocused(true)
                        }}
                        onBlur={() => {
                          setIsFocused(false)
                        }}
                      />
                    </StyledBox>
                  )}
                  isOptionEqualToValue={(option, value) => option.title === value.title}
                />
              </StyledPopperWide>
            </ClickAwayListener>
          )}
          {!isFilterDropdown && isCompleted && val.length === 0 && (
            <ErrorMessage>{inputAndPreviewTitle} cannot be empty</ErrorMessage>
          )}
        </StyledGridItemRelative>
      </Grid>
      <Grid item md={12} lg={6}>
        <SelectedItemsContainer>
          <Box display='flex' alignItems='center' justifyContent='space-between'>
            <InputTitleItalic
              variant='subtitleSmall'
              tabIndex={0}
              aria-label={`selected-${label}-${val.length > 0 && val.length}`}
            >
              Selected {inputAndPreviewTitle}
              {` (${val.length})`}
            </InputTitleItalic>
            {showCustomLabels && (
              <StyledCustomLabels
                variant='CustomSettings'
                onClick={() => {
                  setCustomLabelDialogProps({
                    open: true,
                    section,
                    data:
                      section === SECTIONS.POINT_OF_INTERESTS
                        ? formatPOIs(poiConfig)
                        : formatSectionFilters(filterConfig, section)
                  })
                }}
              >
                Customize Labels
              </StyledCustomLabels>
            )}
          </Box>
          <div className='selectedTags'>
            <StyledChipsContainer>
              {selectAllTitle === 'Entire United States' && allSelected ? EntireUntitedStatesPill : pills}
            </StyledChipsContainer>
          </div>
        </SelectedItemsContainer>
      </Grid>
    </Grid>
  )
}

MultiSelectDropdownComponent.propTypes = {
  selectAllTitle: PropTypes.string.isRequired,
  dropDownTitle: PropTypes.string.isRequired,
  inputAndPreviewTitle: PropTypes.string.isRequired,
  dropDownModifiedTitle: PropTypes.string.isRequired,
  dropDownData: PropTypes.array.isRequired,
  val: PropTypes.array.isRequired,
  setVal: PropTypes.func.isRequired,
  isCompleted: PropTypes.bool.isRequired,
  conditionalProps: PropTypes.object,
  handleOptionDeletion: PropTypes.func,
  hasAnyFilters: PropTypes.bool,
  isFilterDropdown: PropTypes.bool,
  showCustomLabels: PropTypes.bool,
  setCustomLabelDialogProps: PropTypes.func,
  section: PropTypes.string
}

export default MultiSelectDropdownComponent

/*  - Function Descriptions:
  - PopperComponent: A custom popper component for the Autocomplete dropdown.
  - selectorTitle: A function that returns the title of the dropdown based on the selected options.
  - getAriaLabel: A function that returns the aria-label for the dropdown based on the input and preview title.
  - getDropdownOptions: A function that returns the dropdown options based on the input and preview title.
  - handleClick: A function that toggles the dropdown open/close state.
  - handleClose: A function that closes the dropdown.
  - onChipDelete: A function that handles the deletion of selected options (chips).
  - onChange: A function that handles the change event of the Autocomplete component.
  - useEffect: A hook that sets the focus on the Autocomplete component when the dropdown is open.

*/

/*
### Functional Summary of `MultiSelectDropdownComponent.js`

This file defines a React functional component `MultiSelectDropdownComponent` that provides a customizable multi-select dropdown with the following features:

1. **Customizable Dropdown**:
  - Allows users to select multiple options from a dropdown list.
  - Supports a "Select All" option for bulk selection.
  - Displays selected options as removable chips.

2. **Dynamic Titles**:
  - Dynamically updates the dropdown title based on the selected options.
  - Provides specific titles for different input types (e.g., "Health Conditions", "Points of Interest").

3. **Error Handling**:
  - Displays error messages when certain conditions are met (e.g., exceeding a maximum number of selections).

4. **Keyboard Accessibility**:
  - Supports keyboard navigation and actions (e.g., `Enter` to select, `Escape` to close).

5. **Custom Styling**:
  - Uses styled components for consistent and customizable UI elements.

6. **Custom Popper**:
  - Implements a custom popper component for rendering the dropdown.

7. **Customizable Settings**:
  - Includes a button to open a "Customize Selections" popup for advanced settings.

8. **ARIA Accessibility**:
  - Provides ARIA labels for better accessibility and screen reader support.

9. **Integration with Material-UI**:
  - Leverages Material-UI components like `Autocomplete`, `Checkbox`, `Chip`, and `IconButton`.

10. **State Management**:
   - Manages dropdown state (`open`, `isFocused`, `isError`) and selected values (`val`) using React's `useState` hook.

11. **Reusable Functions**:
   - Includes helper functions like `getDropdownOptions`, `selectorTitle`, and `onChipDelete` for modularity and reusability.

12. **Custom Filter Popup**:
   - Integrates a `CustomFilterPopup` component for additional filtering options.

### Key Components and Props:
- **Props**:
  - `selectAllTitle`, `dropDownTitle`, `inputAndPreviewTitle`, `dropDownModifiedTitle`: Strings for dropdown titles.
  - `dropDownData`: Array of dropdown options.
  - `val`: Array of selected values.
  - `setVal`: Function to update selected values.
  - `isCompleted`: Boolean to indicate if the selection is complete.
  - `handleOptionDeletion`: Function to handle deletion of options.

- **Custom Components**:
  - `PopperComponent`: Custom popper for the dropdown.
  - `CustomFilterPopup`: Popup for advanced filtering.

This component is designed to be highly reusable and customizable, making it suitable for various use cases in the application.
*/
